import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PostPerformanceTable = ({ posts, onPostClick }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'engagement', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 10;

  const sortedPosts = [...posts]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = sortedPosts?.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(sortedPosts?.length / postsPerPage);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'desc' ? 'asc' : 'desc'
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return (
      <Icon 
        name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Post Performance Details</h3>
        <p className="text-sm text-muted-foreground mt-1">Comprehensive analytics for all posts</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Post
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('reach')}
              >
                <div className="flex items-center gap-1">
                  Reach
                  <SortIcon columnKey="reach" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('reactions')}
              >
                <div className="flex items-center gap-1">
                  Reactions
                  <SortIcon columnKey="reactions" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('comments')}
              >
                <div className="flex items-center gap-1">
                  Comments
                  <SortIcon columnKey="comments" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('shares')}
              >
                <div className="flex items-center gap-1">
                  Shares
                  <SortIcon columnKey="shares" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('engagementRate')}
              >
                <div className="flex items-center gap-1">
                  Engagement
                  <SortIcon columnKey="engagementRate" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentPosts?.map((post) => (
              <tr key={post?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <Image
                        src={post?.image}
                        alt={post?.imageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground line-clamp-2">{post?.content}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">{post?.date}</span>
                        <span className="px-2 py-0.5 bg-muted text-xs font-medium rounded">{post?.type}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {post?.reach?.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {post?.reactions?.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {post?.comments?.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {post?.shares?.toLocaleString()}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{post?.engagementRate}%</span>
                    <Icon 
                      name={post?.engagementRate > 5 ? 'TrendingUp' : 'TrendingDown'} 
                      size={14} 
                      className={post?.engagementRate > 5 ? 'text-success' : 'text-error'}
                    />
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ExternalLink"
                    onClick={() => onPostClick(post)}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-6 py-4 border-t border-border flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {indexOfFirstPost + 1} to {Math.min(indexOfLastPost, sortedPosts?.length)} of {sortedPosts?.length} posts
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {[...Array(totalPages)]?.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === index + 1
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            iconPosition="right"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostPerformanceTable;